// Fetches and parses events from the published Woodland Google Sheet CSV.
//
// Expected columns:
//   Show Date | Show Title / Lineup | Show Title | Description | Ticket Link | Time | Sugg. $ | Category | Status | Venue Name | Source | Event Link

import { makeEventSlug } from './eventSlug';

function parseCSV(text) {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];

    const headers = lines[0].split(',').map((h) => h.trim());

    return lines.slice(1).map((line) => {
        const values = [];
        let current = '';
        let inQuotes = false;
        for (let i = 0; i < line.length; i++) {
            if (line[i] === '"') {
                inQuotes = !inQuotes;
            } else if (line[i] === ',' && !inQuotes) {
                values.push(current.trim());
                current = '';
            } else {
                current += line[i];
            }
        }
        values.push(current.trim());

        const row = {};
        headers.forEach((header, i) => {
            row[header] = values[i] || '';
        });
        return row;
    });
}

export async function fetchSheetEvents(sheetUrl) {
    if (!sheetUrl) return { futureShows: [], pastShows: [] };

    const res = await fetch(sheetUrl);
    if (!res.ok) throw new Error(`Failed to fetch sheet: ${res.status}`);

    const text = await res.text();
    const rows = parseCSV(text);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const todayMs = today.getTime();

    const shows = rows
        .filter((row) => row['Show Date'])
        .map((row, i) => {
            const show = {
                id: `sheet-${i}`,
                date: row['Show Date'],
                show_title: row['Show Title'] || '',
                performers: row['Show Title / Lineup']
                    ? row['Show Title / Lineup'].split(',').map((p) => p.trim()).filter(Boolean)
                    : [],
                description: row['Description'] || '',
                categories: row['Category'] || '',
                ticket_link: row['Ticket Link'] || '',
                event_link: row['Event Link'] || '',
                start_time: row['Time'] || '',
                cost: row['Sugg. $'] || '',
                venue: row['Venue Name'] || 'Woodland Theater',
            };
            show.slug = makeEventSlug(show);
            return show;
        })
        .sort((a, b) => Date.parse(a.date + 'T12:00:00') - Date.parse(b.date + 'T12:00:00'));

    const woodlandShows = shows.filter((s) => !s.venue || s.venue === 'Woodland Theater');
    const otherVenueShows = shows.filter((s) => s.venue && s.venue !== 'Woodland Theater');

    const parseDate = (d) => Date.parse(d + 'T12:00:00');

    const futureShows = woodlandShows.filter((s) => parseDate(s.date) >= todayMs);
    const pastShows = [...woodlandShows]
        .filter((s) => parseDate(s.date) < todayMs)
        .sort((a, b) => parseDate(b.date) - parseDate(a.date));

    const pastVenueShows = [...otherVenueShows]
        .filter((s) => parseDate(s.date) < todayMs)
        .sort((a, b) => parseDate(b.date) - parseDate(a.date));

    return { futureShows, pastShows, pastVenueShows };
}
